<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  previewResult: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();

const summaryCards = computed(() => {
  const summary = props.previewResult?.summary || {};
  const cards = [
    {
      key: 'TOTAL_CONVERSATIONS',
      value: summary.total_conversations,
      label: t(
        'INBOX_MGMT.CONVERSATION_TRANSFER.PREVIEW.SUMMARY.TOTAL_CONVERSATIONS'
      ),
    },
    {
      key: 'TOTAL_CONTACTS',
      value: summary.total_contacts,
      label: t(
        'INBOX_MGMT.CONVERSATION_TRANSFER.PREVIEW.SUMMARY.TOTAL_CONTACTS'
      ),
    },
    {
      key: 'MESSAGES_TO_MOVE',
      value: summary.messages_to_move,
      label: t(
        'INBOX_MGMT.CONVERSATION_TRANSFER.PREVIEW.SUMMARY.MESSAGES_TO_MOVE'
      ),
    },
    {
      key: 'OPEN_CONVERSATIONS',
      value: summary.open_conversations,
      label: t(
        'INBOX_MGMT.CONVERSATION_TRANSFER.PREVIEW.SUMMARY.OPEN_CONVERSATIONS'
      ),
    },
    {
      key: 'RESOLVED_CONVERSATIONS',
      value: summary.resolved_conversations,
      label: t(
        'INBOX_MGMT.CONVERSATION_TRANSFER.PREVIEW.SUMMARY.RESOLVED_CONVERSATIONS'
      ),
    },
    {
      key: 'SKIPPED_CONVERSATIONS',
      value: summary.skipped_conversations,
      label: t(
        'INBOX_MGMT.CONVERSATION_TRANSFER.PREVIEW.SUMMARY.SKIPPED_CONVERSATIONS'
      ),
    },
  ];
  return cards.filter(card => card.value !== undefined && card.value !== null);
});

const blockingErrors = computed(
  () => props.previewResult?.blocking_errors || []
);

const warnings = computed(() => props.previewResult?.warnings || []);

const hasResult = computed(() => Boolean(props.previewResult));
const hasBlocking = computed(() => blockingErrors.value.length > 0);
</script>

<template>
  <div class="flex flex-col gap-4">
    <h4 class="text-sm font-medium text-n-slate-12">
      {{ $t('INBOX_MGMT.CONVERSATION_TRANSFER.PREVIEW.TITLE') }}
    </h4>

    <p v-if="!hasResult" class="text-sm text-n-slate-11">
      {{ $t('INBOX_MGMT.CONVERSATION_TRANSFER.PREVIEW.EMPTY') }}
    </p>

    <div
      v-else
      class="flex flex-col gap-4"
      :class="{ 'opacity-90': hasBlocking }"
    >
      <div
        v-if="summaryCards.length"
        class="grid grid-cols-2 gap-3 sm:grid-cols-3"
      >
        <div
          v-for="card in summaryCards"
          :key="card.key"
          class="p-3 rounded-md border border-n-weak bg-n-alpha-black1"
        >
          <p class="mb-1 text-xs uppercase text-n-slate-11">
            {{ card.label }}
          </p>
          <p class="text-lg font-semibold text-n-slate-12">{{ card.value }}</p>
        </div>
      </div>

      <div
        v-if="blockingErrors.length"
        class="p-3 rounded-md border border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-900/20"
      >
        <p class="mb-2 text-sm font-semibold text-red-700 dark:text-red-300">
          {{
            $t('INBOX_MGMT.CONVERSATION_TRANSFER.PREVIEW.BLOCKING_ERRORS_TITLE')
          }}
        </p>
        <ul class="pl-4 list-disc text-sm text-red-700 dark:text-red-200">
          <li v-for="(error, index) in blockingErrors" :key="`err-${index}`">
            {{ error.message || error.code || error }}
          </li>
        </ul>
        <p class="mt-2 text-xs text-red-700 dark:text-red-300">
          {{ $t('INBOX_MGMT.CONVERSATION_TRANSFER.PREVIEW.BLOCKING_HINT') }}
        </p>
      </div>

      <div
        v-if="warnings.length"
        class="p-3 rounded-md border border-yellow-300 bg-yellow-50 dark:border-yellow-900 dark:bg-yellow-900/20"
      >
        <p
          class="mb-2 text-sm font-semibold text-yellow-800 dark:text-yellow-200"
        >
          {{ $t('INBOX_MGMT.CONVERSATION_TRANSFER.PREVIEW.WARNINGS_TITLE') }}
        </p>
        <ul class="pl-4 list-disc text-sm text-yellow-800 dark:text-yellow-100">
          <li v-for="(warning, index) in warnings" :key="`warn-${index}`">
            {{ warning.message || warning.code || warning }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
